/**
 * HTTP REST client for EthPending API
 */

import { EventEmitter } from './EventEmitter';
import { FilterManager } from './FilterManager';
import { MetricsCollector } from './utils/metrics';
import { retryWithBackoff } from './utils/retry';
import type {
  FilterOptions,
  HTTPResponse,
  Metrics,
  HTTPClientEvents,
} from './types';
import {
  ApiError,
  RateLimitError,
  AuthenticationError,
} from './types';

const DEFAULT_HTTP_URL = 'https://api.ethpending.com';

interface FetchOptions {
  addresses?: string[];
  methods?: string[];
  limit?: number;
}

/**
 * HTTP client for polling pending transactions
 */
export class HTTPClient extends EventEmitter<HTTPClientEvents> {
  private apiKey: string;
  private baseUrl: string;
  private filterManager: FilterManager;
  private metrics: MetricsCollector;

  constructor(apiKey: string, baseUrl?: string) {
    super();
    this.apiKey = apiKey;
    this.baseUrl = baseUrl || DEFAULT_HTTP_URL;
    this.filterManager = new FilterManager();
    this.metrics = new MetricsCollector();
  }

  /**
   * Fetch pending transactions from the API
   * @param options - Optional filter options and limit
   * @returns API response with transactions
   */
  async fetchTransactions(options: FetchOptions = {}): Promise<HTTPResponse> {
    // Merge options with current filters
    const filters: FilterOptions = {
      addresses: options.addresses || this.filterManager.getFilters().addresses,
      methods: options.methods || this.filterManager.getFilters().methods,
    };

    const url = this.buildUrl('/transactions', filters, options.limit);
    
    const startTime = Date.now();

    try {
      const response = await this.fetchWithRetry(url);
      
      const latency = Date.now() - startTime;
      this.metrics.recordLatency(latency);
      
      return response;
    } catch (error) {
      this.metrics.recordError(error instanceof Error ? error.name : 'UnknownError');
      throw error;
    }
  }

  /**
   * Set default filters for all requests
   * @param filters - Filter options
   */
  setFilters(filters: FilterOptions): void {
    this.filterManager.setFilters(filters);
  }

  /**
   * Get current filters
   */
  getFilters(): FilterOptions {
    return this.filterManager.getFilters();
  }

  /**
   * Get current metrics
   */
  getMetrics(): Metrics {
    return this.metrics.getMetrics();
  }

  /**
   * Build request URL with query parameters
   */
  private buildUrl(path: string, filters: FilterOptions, limit?: number): string {
    const url = new URL(path, this.baseUrl);
    
    // Add filters to query params
    if (filters.addresses && filters.addresses.length > 0) {
      filters.addresses.forEach(addr => url.searchParams.append('addresses', addr));
    }
    
    if (filters.methods && filters.methods.length > 0) {
      filters.methods.forEach(method => url.searchParams.append('methods', method));
    }
    
    if (limit !== undefined) {
      url.searchParams.append('limit', limit.toString());
    }
    
    return url.toString();
  }

  /**
   * Fetch with retry logic for rate limits and transient errors
   */
  private async fetchWithRetry(url: string): Promise<HTTPResponse> {
    return retryWithBackoff(
      async () => {
        const response = await this.makeRequest(url);
        this.metrics.recordMessageReceived();
        return response;
      },
      {
        initialDelay: 1000,
        maxDelay: 30000,
        maxAttempts: 3,
      }
    );
  }

  /**
   * Make HTTP request to the API
   */
  private async makeRequest(url: string): Promise<HTTPResponse> {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${this.apiKey}`,
        'Content-Type': 'application/json',
      },
    });

    // Handle different response statuses
    if (response.status === 401) {
      throw new AuthenticationError('Invalid API key');
    }

    if (response.status === 429) {
      const retryAfter = response.headers.get('Retry-After');
      const retryAfterMs = retryAfter ? parseInt(retryAfter, 10) * 1000 : 60000;
      
      this.emit('rateLimit', retryAfterMs);
      throw new RateLimitError('Rate limit exceeded', retryAfterMs);
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = (errorData as any).error || `HTTP error ${response.status}`;
      throw new ApiError(message, response.status);
    }

    const data = await response.json();
    
    // Validate response structure
    if (typeof data !== 'object' || data === null) {
      throw new ApiError('Invalid response format');
    }

    return data as HTTPResponse;
  }

  /**
   * Fetch a single page of transactions
   * @param _page - Page number (0-indexed) - currently unused as the API doesn't support pagination
   * @param pageSize - Number of transactions per page
   * @param options - Optional filter options
   */
  async fetchPage(
    _page: number,
    pageSize: number,
    options: FetchOptions = {}
  ): Promise<HTTPResponse> {
    return this.fetchTransactions({
      ...options,
      limit: pageSize,
    });
  }

  /**
   * Poll for new transactions at a regular interval
   * @param intervalMs - Polling interval in milliseconds
   * @param callback - Callback function to handle new transactions
   * @param options - Optional filter options
   * @returns Function to stop polling
   */
  startPolling(
    intervalMs: number,
    callback: (response: HTTPResponse) => void,
    options: FetchOptions = {}
  ): () => void {
    let isPolling = true;

    const poll = async () => {
      if (!isPolling) {
        return;
      }

      try {
        const response = await this.fetchTransactions(options);
        callback(response);
      } catch (error) {
        this.emit('error', error instanceof Error ? error : new Error(String(error)));
      }

      if (isPolling) {
        setTimeout(poll, intervalMs);
      }
    };

    // Start polling
    poll();

    // Return stop function
    return () => {
      isPolling = false;
    };
  }
}

